import React, { useEffect, useRef, useState } from "react";
import { uploadChunked as uploadChunkedLib } from "../lib/upload";
import { authStore } from "../lib/api";
import { useNavigate, useParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { toast } from "../components/ui/sonner";
import { Button } from "../components/ui/button";
import { ArrowLeft, Camera, ClipboardCheck, HardHat, Loader2, Plus, ShieldCheck, Trash2, TrendingUp, X, CheckCircle2, XCircle, MinusCircle, AlertTriangle } from "lucide-react";
import { SubCheckinPanel } from "../components/SubCheckinPanel";
import { OwnerDigestPanel } from "../components/OwnerDigestPanel";
import { VoiceFill, mergeVoiceFill } from "../components/VoiceFill";
import { fmtD } from "../lib/date";

import { T } from "../lib/i18n";

const fileUrl = (fid) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${fid}/download?auth=${encodeURIComponent(authStore.get("ali_token") || "")}`;

async function uploadChunked(api, file) {
  return (await uploadChunkedLib(api, file, { type: file.type || "image/jpeg" })).fileId;
}

const RESULTS = [["pass", "合格", CheckCircle2, "bg-emerald-600 text-white border-emerald-600"], ["fail", "不合格", XCircle, "bg-red-600 text-white border-red-600"], ["na", "不适用", MinusCircle, "bg-slate-500 text-white border-slate-500"]];

const PhotoBtn = ({ api, ids, onChange, tid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try { const out = []; for (const f of files) out.push(await uploadChunked(api, f)); onChange([...(ids || []), ...out]); } catch (err) { toast.error(err?.response?.data?.detail || T("上传失败")); }
    setBusy(false);
    e.target.value = "";
  };
  return (
    <span className="inline-flex items-center gap-1">
      <input ref={ref} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={pick} data-testid={`${tid}-photo-input`} />
      <button type="button" data-testid={`${tid}-photo-btn`} onClick={() => ref.current?.click()} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}{ids?.length ? T("{0} 张", [ids.length]) : T("拍照")}</button>
      {(ids || []).map((id) => <a key={id} href={fileUrl(id)} target="_blank" rel="noreferrer"><img src={fileUrl(id)} alt="" className="h-7 w-7 rounded object-cover border" /></a>)}
    </span>
  );
};

const CheckItem = ({ x, onChange, trades, api, tid }) => (
  <div className={`rounded-xl border p-2.5 ${x.result === "fail" ? "border-red-300 bg-red-50/40" : x.result === "pass" ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200 bg-white"}`} data-testid={tid} data-result={x.result}>
    <div className="flex items-start gap-2 flex-wrap">
      <p className="text-sm font-medium text-slate-800 flex-1 min-w-[160px]">{T(x.label)}</p>
      <div className="flex gap-1">
        {RESULTS.map(([r, zh, Icon, cls]) => (
          <button key={r} type="button" data-testid={`${tid}-${r}`} onClick={() => onChange({ ...x, result: r })} className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md border ${x.result === r ? cls : "border-slate-200 text-slate-500 bg-white hover:border-slate-400"}`}><Icon className="h-3 w-3" />{T(zh)}</button>
        ))}
      </div>
    </div>
    {x.result === "fail" && (
      <div className="mt-2 grid sm:grid-cols-2 gap-2">
        <input data-testid={`${tid}-note`} value={x.note || ""} onChange={(e) => onChange({ ...x, note: e.target.value })} placeholder={T("问题描述（将写入整改任务 / 阻塞项）")} className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white" />
        <div className="flex items-center gap-2">
          <select data-testid={`${tid}-assign`} value={x.assignName || ""} onChange={(e) => { const t = trades.find((t) => t.sub === e.target.value); onChange({ ...x, assignName: e.target.value, trade: t?.trade || x.trade || "", assignPhone: t?.phone || "" }); }} className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white flex-1">
            <option value="">{T("责任方（分包 / 自有班组）")}</option>
            {trades.map((t) => <option key={`${t.trade}-${T(t.sub)}`} value={t.sub}>{T(t.sub)}{t.trade ? ` · ${t.trade}` : ""}</option>)}
          </select>
          <PhotoBtn api={api} ids={x.photoIds} onChange={(ids) => onChange({ ...x, photoIds: ids })} tid={tid} />
        </div>
      </div>
    )}
  </div>
);

const Score = ({ v, label, tid }) => (
  <div className="text-center" data-testid={tid}>
    <p className={`text-2xl font-bold ${v == null ? "text-slate-300" : v >= 90 ? "text-emerald-600" : v >= 75 ? "text-amber-600" : "text-red-600"}`}>{v == null ? "—" : v}</p>
    <p className="text-[10px] text-slate-500">{label}</p>
  </div>
);

/**
 * SiteInspection —— 现场巡检（进度 / 质量 / 安全）
 *
 * 两种形态（2026-10-15 抽屉化改造）：整页态（路由 /project/:id/inspection）
 * 与 embedded（GlobalProjectDrawer 第 1 层抽屉，id 走 projectId prop、返回走 onClose）。
 */
export default function SiteInspection({ embedded = false, projectId: propId, onClose } = {}) {
  const { id: routeId } = useParams();
  const id = propId || routeId;
  const { api, lang, user } = useApp();
  const zh = lang === "zh";
  const nav = useNavigate();
  const [tpl, setTpl] = useState(null);
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const canInspect = ["admin", "ceo", "eng_manager", "pm", "member", "cdc"].includes(user?.role);

  const load = async () => {
    try {
      const [t, l] = await Promise.all([api.get(`/projects/${id}/site-inspections/template`), api.get(`/projects/${id}/site-inspections`)]);
      setTpl(t.data); setList(l.data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const startForm = () => {
    setResult(null);
    setForm({
      date: tpl.today, weather: "", note: "", photoIds: [],
      progress: (tpl.trades.length ? tpl.trades : [{ trade: "", sub: "" }]).map((t) => ({ trade: t.trade, sub: t.sub, pct: 0, crew: Number(t.crew) || 0, note: "" })),
      quality: tpl.quality.map((q) => ({ ...q, result: "na", note: "", photoIds: [], assignName: "", trade: "" })),
      safety: tpl.safety.map((s) => ({ ...s, result: "na", note: "", photoIds: [], assignName: "", trade: "" })),
    });
    setShowForm(true);
  };
  const setAll = (kind, r) => setForm((f) => ({ ...f, [kind]: f[kind].map((x) => ({ ...x, result: r })) }));
  const upd = (kind, i, v) => setForm((f) => ({ ...f, [kind]: f[kind].map((x, j) => (j === i ? v : x)) }));
  const submit = async () => {
    const fails = [...form.quality, ...form.safety].filter((x) => x.result === "fail");
    if (fails.length && !window.confirm(zh ? `有 ${fails.length} 项不合格：质量不合格将自动生成整改任务（Punch）通知分包，安全隐患将登记为阻塞项并通知工程经理。确认提交？` : `${fails.length} failed items will create punch / blockers. Submit?`)) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/projects/${id}/site-inspections`, { ...form, progress: form.progress.filter((p) => p.trade || p.sub) });
      setResult(data); setShowForm(false); toast.success(zh ? "巡检已提交" : "Inspection saved"); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setSaving(false);
  };

  if (!tpl) return <div className="flex justify-center py-20" data-testid="si-loading"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;
  const last = list[0];

  return (
    <div className="space-y-4 max-w-4xl mx-auto" data-testid="si-page">
      <div className="flex items-start gap-3 flex-wrap">
        {/* 返回：整页态回项目详情（原行为）；抽屉态关本层抽屉，露出下面的项目详情抽屉 */}
        <button data-testid="si-back" onClick={() => (embedded && onClose ? onClose() : nav(`/project/${id}`))} className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"><ArrowLeft className="h-4 w-4" /></button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-slate-900 flex items-center gap-2"><HardHat className="h-5 w-5 text-amber-600" />{zh ? "现场巡检 · 进度 / 质量 / 安全" : "Site Inspection"}</h1>
          <p className="text-xs text-slate-500 truncate">{tpl.address} · {T(tpl.phaseName)}{tpl.manager && tpl.manager !== "-" ? ` · PM ${tpl.manager}` : ""}</p>
        </div>
        {canInspect && !showForm && <Button data-testid="si-new-btn" onClick={startForm} className="bg-slate-900 hover:bg-slate-700 h-9 text-xs"><Plus className="h-3.5 w-3.5 mr-1" />{zh ? "新建今日巡检" : "New inspection"}</Button>}
      </div>

      {result && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="si-result">
          <p className="text-sm font-bold text-emerald-800 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" />{zh ? "巡检已提交" : "Submitted"} · Q {result.inspection.scores.quality ?? "—"} / S {result.inspection.scores.safety ?? "—"}</p>
          {result.issues.length > 0 ? (
            <ul className="mt-2 space-y-1">{result.issues.map((i, k) => <li key={k} className="text-xs text-slate-700 flex items-center gap-1.5" data-testid={`si-result-issue-${k}`}><AlertTriangle className={`h-3.5 w-3.5 ${i.kind === "safety" ? "text-red-600" : "text-amber-600"}`} />{i.kind === "safety" ? T("安全隐患 → 阻塞项") : T("质量不合格 → 整改任务")}：{T(i.label)}{i.assignName ? ` · ${i.assignName}` : ""}</li>)}</ul>
          ) : <p className="text-xs text-emerald-700 mt-1">{zh ? "无不合格项" : "No issues"}</p>}
        </div>
      )}

      {showForm && form && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-5" data-testid="si-form">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <input type="date" data-testid="si-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="text-xs border border-slate-200 rounded-md px-2 py-1.5" />
              <input data-testid="si-weather" value={form.weather} onChange={(e) => setForm({ ...form, weather: e.target.value })} placeholder={zh ? "天气" : "Weather"} className="text-xs border border-slate-200 rounded-md px-2 py-1.5 w-24" />
            </div>
            <button data-testid="si-form-close" onClick={() => setShowForm(false)} className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
          </div>

          <VoiceFill api={api} pid={id} tpl={tpl} zh={zh} onFill={(data) => setForm((f) => mergeVoiceFill(f, data))} />

          <section data-testid="si-progress-section">
            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5 mb-2"><TrendingUp className="h-4 w-4 text-sky-600" />{zh ? "进度 · 在场工序" : "Progress"}</h3>
            <div className="space-y-2">
              {form.progress.map((p, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`si-progress-row-${i}`}>
                  <input data-testid={`si-progress-trade-${i}`} value={p.trade} onChange={(e) => upd("progress", i, { ...p, trade: e.target.value })} placeholder={zh ? "工序" : "Trade"} className="col-span-3 text-xs border border-slate-200 rounded-md px-2 py-1.5" />
                  <input data-testid={`si-progress-sub-${i}`} value={p.sub} onChange={(e) => upd("progress", i, { ...p, sub: e.target.value })} placeholder={zh ? "分包 / 班组" : "Sub"} className="col-span-3 text-xs border border-slate-200 rounded-md px-2 py-1.5" />
                  <div className="col-span-3 flex items-center gap-1"><input type="range" min="0" max="100" step="5" data-testid={`si-progress-pct-${i}`} value={p.pct} onChange={(e) => upd("progress", i, { ...p, pct: Number(e.target.value) })} className="flex-1 accent-sky-600" /><span className="text-xs font-bold w-9 text-right">{p.pct}%</span></div>
                  <input type="number" min="0" data-testid={`si-progress-crew-${i}`} value={p.crew} onChange={(e) => upd("progress", i, { ...p, crew: e.target.value })} placeholder={zh ? "人数" : "Crew"} className="col-span-2 text-xs border border-slate-200 rounded-md px-2 py-1.5" />
                  <button type="button" data-testid={`si-progress-del-${i}`} onClick={() => setForm({ ...form, progress: form.progress.filter((_, j) => j !== i) })} className="col-span-1 text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              <button type="button" data-testid="si-progress-add" onClick={() => setForm({ ...form, progress: [...form.progress, { trade: "", sub: "", pct: 0, crew: 0, note: "" }] })} className="text-[11px] text-sky-700 hover:underline inline-flex items-center gap-1"><Plus className="h-3 w-3" />{zh ? "添加工序" : "Add trade"}</button>
            </div>
          </section>

          {[["quality", T("质量检查"), ClipboardCheck, "text-violet-600"], ["safety", T("安全检查（OSHA 要点）"), ShieldCheck, "text-red-600"]].map(([kind, title, Icon, cls]) => (
            <section key={kind} data-testid={`si-${kind}-section`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><Icon className={`h-4 w-4 ${cls}`} />{title}<span className="text-[10px] text-slate-400 font-normal">· {T(tpl.phaseName)}</span></h3>
                <button type="button" data-testid={`si-${kind}-all-pass`} onClick={() => setAll(kind, "pass")} className="text-[11px] text-emerald-700 hover:underline">{zh ? "全部合格" : "All pass"}</button>
              </div>
              <div className="space-y-2">{form[kind].map((x, i) => <CheckItem key={x.key} x={x} trades={tpl.trades.length ? tpl.trades : form.progress.filter((p) => p.sub).map((p) => ({ trade: p.trade, sub: p.sub, phone: "" }))} api={api} tid={`si-${kind}-${x.key}`} onChange={(v) => upd(kind, i, v)} />)}</div>
            </section>
          ))}

          <section className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap"><span className="text-xs font-bold text-slate-700">{zh ? "现场照片" : "Photos"}</span><PhotoBtn api={api} ids={form.photoIds} onChange={(ids) => setForm({ ...form, photoIds: ids })} tid="si-photos" /></div>
            <textarea data-testid="si-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} placeholder={zh ? "巡检备注 / 明日计划 / 需要配合事项" : "Notes"} className="w-full text-xs border border-slate-200 rounded-md px-2 py-1.5" />
          </section>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="si-cancel" onClick={() => setShowForm(false)} className="h-9 text-xs">{zh ? "取消" : "Cancel"}</Button>
            <Button data-testid="si-submit" disabled={saving} onClick={submit} className="bg-slate-900 hover:bg-slate-700 h-9 text-xs">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (zh ? "提交巡检" : "Submit")}</Button>
          </div>
        </div>
      )}

      <SubCheckinPanel api={api} id={id} zh={zh} user={user} />
      <OwnerDigestPanel api={api} id={id} zh={zh} user={user} refreshTick={list.length} />

      <div className="rounded-2xl border border-slate-200 bg-white" data-testid="si-history">
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
          <p className="text-xs font-bold text-slate-700">{zh ? "巡检记录" : "History"} <span className="text-slate-400 font-normal">· {list.length}</span></p>
          {last && <div className="flex gap-4"><Score v={last.scores?.quality} label={zh ? "最近质量" : "Quality"} tid="si-last-quality" /><Score v={last.scores?.safety} label={zh ? "最近安全" : "Safety"} tid="si-last-safety" /></div>}
        </div>
        {list.length === 0 && <p className="text-center text-sm text-slate-400 py-10" data-testid="si-empty">{zh ? "尚无巡检记录，点「新建今日巡检」开始" : "No inspections yet"}</p>}
        {list.map((x) => (
          <div key={x.id} className="px-4 py-3 border-b border-slate-50 last:border-0" data-testid={`si-item-${x.id}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-sm font-semibold text-slate-800">{fmtD(x.date)} <span className="text-xs text-slate-400 font-normal">· {T(x.phaseName)} · {x.by}{x.weather ? ` · ${x.weather}` : ""}{x.crewTotal ? T(" · {0} 人", [x.crewTotal]) : ""}</span></p>
              <span className={`text-xs font-bold ${(x.scores?.quality ?? 100) < 80 ? "text-red-600" : "text-emerald-700"}`}>Q {x.scores?.quality ?? "—"}</span>
              <span className={`text-xs font-bold ${(x.scores?.safety ?? 100) < 80 ? "text-red-600" : "text-emerald-700"}`}>S {x.scores?.safety ?? "—"}</span>
              {x.issues?.length > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700" data-testid={`si-item-issues-${x.id}`}>{x.issues.length} {zh ? "项不合格" : "issues"}</span>}
            </div>
            {x.progress?.length > 0 && <p className="text-[11px] text-slate-500 mt-1">{x.progress.map((p) => `${p.trade || p.sub} ${p.pct}%${p.crew ? `·${p.crew}人` : ""}`).join(" / ")}</p>}
            {x.issues?.length > 0 && <ul className="mt-1 space-y-0.5">{x.issues.map((i, k) => <li key={k} className="text-[11px] text-slate-600 flex items-center gap-1"><AlertTriangle className={`h-3 w-3 ${i.kind === "safety" ? "text-red-600" : "text-amber-600"}`} />{T(i.label)}{i.assignName ? ` · ${i.assignName}` : ""}</li>)}</ul>}
            {x.note && <p className="text-[11px] text-slate-500 mt-1">{T(x.note)}</p>}
            {(x.photoIds || []).length > 0 && <div className="flex gap-1 mt-1.5">{x.photoIds.slice(0, 8).map((f) => <a key={f} href={fileUrl(f)} target="_blank" rel="noreferrer"><img src={fileUrl(f)} alt="" className="h-12 w-12 rounded-md object-cover border" /></a>)}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
